import React from 'react'
import { Modal } from 'antd'
import { Card, CardBody } from './other/card/card'
import generateExcel from '../helpers/generateExcel'
import { dateNow } from '../helpers/date'

const StatCard = ({ label, value, textClass }) => (
  <Card>
    <CardBody className='p-3 text-center'>
      <div className={`fs-2 fw-bold ${textClass}`}>{value ?? 0}</div>
      <div className='text-muted small mt-1'>{label}</div>
    </CardBody>
  </Card>
)

const getErrorMessage = (details) => {
  if (details?.error_message) return details.error_message
  if (details?.error) return details.error
  if (Array.isArray(details?.errors) && details.errors.length > 0) {
    return details.errors.map(e => `${e.field}: ${e.message}`).join(' | ')
  }
  return details?.message ?? ''
}

// exported for the test; rows feed the Excel report
export const buildErrorRows = (failedDetailEntries, errorList) => [
  ...failedDetailEntries.map(([url, details]) => ({
    URL: url,
    Reason: getErrorMessage(details)
  })),
  ...errorList.map(e => ({
    URL: e?.url ?? '',
    Reason: e?.reason ?? getErrorMessage(e)
  }))
]

const InsertionResultModal = ({ result, reportTitle, onClose }) => {
  if (!result) return null

  // ponytail: marking endpoints nest stats under `data`, insertion ones are flat
  const flat = { ...result, ...(result.data ?? {}) }

  const {
    message,
    new_links,
    inserted_links,
    marked_links,
    existing_links,
    failed_links,
    invalid_domains,
    not_found_links,
    not_found_domains = [],
    failed_links_detail = {},
    errors = []
  } = flat

  const newLinksCount = new_links ?? inserted_links ?? marked_links ?? 0
  const invalidCount = invalid_domains ?? not_found_links
  const failedDetailEntries = Object.entries(failed_links_detail ?? {})
  const errorList = Array.isArray(errors) ? errors : []
  const hasErrors =
    failedDetailEntries.length > 0 ||
    errorList.length > 0 ||
    not_found_domains.length > 0 ||
    (failed_links ?? 0) > 0 ||
    (invalidCount ?? 0) > 0

  const handleDownloadReport = () => {
    const rows = buildErrorRows(failedDetailEntries, errorList)

    generateExcel(
      rows.length > 0
        ? rows
        : [{ URL: '', Reason: 'No detail available' }],
      `${reportTitle ?? 'insertion'} errors ${dateNow()}`
    )
  }

  return (
    <Modal
      open={!!result}
      onCancel={onClose}
      title='Submission Result'
      className='custom-modal'
      footer={
        <div className='d-flex justify-content-end gap-2'>
          {hasErrors && (
            <button
              className='btn btn-outline-danger'
              onClick={handleDownloadReport}
            >
              <i className='fas fa-download me-1' />
              Download Error Report
            </button>
          )}
          <button className='btn btn-theme' onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      {message && <p className='text-muted small mb-3'>{message}</p>}
      <div className='row g-3 mb-2'>
        <div className='col-6'>
          <StatCard
            label={marked_links != null ? 'Marked Links' : 'New Links'}
            value={newLinksCount}
            textClass='text-success'
          />
        </div>
        <div className='col-6'>
          <StatCard label='Existing Links' value={existing_links} textClass='text-theme' />
        </div>
        <div className='col-6'>
          <StatCard label='Failed Links' value={failed_links} textClass='text-danger' />
        </div>
        <div className='col-6'>
          <StatCard
            label={not_found_links != null ? 'Not Found Links' : 'Invalid Domains'}
            value={invalidCount}
            textClass='text-warning'
          />
        </div>
      </div>
      {not_found_domains.length > 0 && (
        <div className='mt-3'>
          <p className='fw-semibold small mb-2'>Not Found Domains</p>
          <ul className='list-unstyled small mb-0'>
            {not_found_domains.map((item, i) => (
              <li key={i} className='mb-1'>
                <i className='fas fa-circle-xmark text-danger me-2' />
                <span className='text-muted'>{item?.domain ?? item}</span>
                {item?.url && (
                  <span className='text-muted ms-1 opacity-50'>— {item.url}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  )
}

export default InsertionResultModal
